import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Heart,
  Shield,
  PawPrint,
  ArrowRight,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Quote,
  X
} from "lucide-react";
import ScrollingBanner from "../components/ScrollingBanner";
import FacebookIcon from "../components/icon/FacebookIcon";
import InstagramIcon from "../components/icon/InstagramIcon";
import OurMissionSection from "../components/OurMissionSection";
import ImpactStatsSection from "../components/ImpactStatsSection";
import groseille from "../images/groseille.webp";
import rousette from "../images/rousette.webp";
import { Helmet } from "react-helmet-async";
import clippath from "../images/Clip-path.webp";
import ImgHero from "../images/chat-homepage-hero2.webp"
import ImgHero2 from "../images/chat-homepage-hero-2.webp";
import ImgHero3 from "../images/chat-homepage-hero-3.webp";
import ImgHero4 from "../images/chat-homepage-hero-4.webp";
import ImgHero5 from "../images/chat-homepage-hero-5.webp";
import ImgHero6 from "../images/chat-homepage-hero-6.webp";
import ImgRouxDuo from "../images/chat-roux-duo.webp"
import ImgChatonRoux from "../images/chaton-roux.webp"
import ImgChatonNoir from "../images/chaton-noir.webp"
import ImgBoutiqueSolidaire from "../images/boutique-solidaire.webp"
import ImgChatonGoutiereGrisBlanc from "../images/chaton-goutiere-gris-blanc.webp"
import ImgChatSiamois from "../images/chat-siamois.webp"
import ImgDons from "../images/dons-ponctuels.webp"
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function SavoinimauxLandingPage() {
const containerRef = useRef(null);
const heroTitleRef = useRef(null);
const heroTextRef = useRef(null);
const heroActionsRef = useRef(null);
const heroVisualRef = useRef(null);
const bgGlow1Ref = useRef(null);
const bgGlow2Ref = useRef(null);
const bgGlow3Ref = useRef(null);
const pawTrailRef = useRef(null);
const sectionRefs = useRef([]);
const [donModalOpen, setDonModalOpen] = useState(false);
const [adhesionModalOpen, setAdhesionModalOpen] = useState(false);

const [cats, setCats] = useState([]);

useEffect(() => {
  fetch("/data/adoptable-cats.json")
    .then((res) => res.json())
    .then((data) => setCats(data))
    .catch(() => setCats([]));
}, []);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const stories = useMemo(
    () => [
      {
        name: "Roucette",
        image: rousette,
        title: "Voir sans les yeux, aimer sans limite.",
        text: "Après des années au sein d’un centre équestre, Roucette a été prise en charge par l’association alors que sa santé se dégradait. Devenue aveugle suite à une lourde opération, elle a pourtant retrouvé une vie remplie d’amour dans sa famille d’accueil. Entourée, rassurée et profondément attachante, elle a vécu ses derniers moments dans la douceur, laissant derrière elle une empreinte inoubliable.",
      },
      {
        name: "Groseille & ses 7 chatons",
        image: groseille,
        title: "Sept chatons, tous sauvés.",
        text: "Minette errante et gestante, Groseille a été trappée puis mise en sécurité par nos bénévoles. Quelques jours plus tard, elle donnait naissance à 7 chatons au chaud, dans sa famille d’accueil. Aujourd’hui, Groseille est stérilisée, en sécurité, et ses petits ont tous trouvé une famille.",
      },
    ],
    []
  );

  const [storyIndex, setStoryIndex] = useState(0)

  const heroImages = [ImgHero, ImgHero2, ImgHero3, ImgHero4, ImgHero5, ImgHero6];
  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const [contactForm, setContactForm] = useState({
    fullName: "",
    department: "",
    phone: "",
    email: "",
    message: "",
  });

  const [contactStatus, setContactStatus] = useState({
    loading: false,
    success: false,
    error: "",
  });

  const handleContactChange = (field, value) => {
    setContactForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    setContactStatus({
      loading: true,
      success: false,
      error: "",
    });

    try {
      if (
        !contactForm.fullName.trim() ||
        !contactForm.email.trim() ||
        !contactForm.message.trim()
      ) {
        throw new Error(
          "Merci de remplir au minimum le nom, l’e-mail et le message."
        );
      }

      const response = await fetch("https://formspree.io/f/xgorvzjl", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          nom: contactForm.fullName,
          departement: contactForm.department,
          telephone: contactForm.phone,
          email: contactForm.email,
          message: contactForm.message,
        }),
      });

      if (!response.ok) {
        throw new Error("L’envoi du message a échoué. Réessaie dans un instant.");
      }

      setContactStatus({
        loading: false,
        success: true,
        error: "",
      });

      setContactForm({
        fullName: "",
        department: "",
        phone: "",
        email: "",
        message: "",
      });
   } catch (error) {
      setContactStatus({
        loading: false,
        success: false,
        error: error?.message || "Une erreur est survenue.",
      });
    }
  };

  const helpingOptions = [
  {
    title: "Dons ponctuels",
    description:
      "Frais vétérinaires, alimentation, équipements et dépenses essentielles du quotidien.",
    image: ImgChatonNoir,
    type: "modal",
  },
  {
    title: "1€ / mois sur Teaming",
    description:
      "Un euro par mois pour faire la différence dans leur quotidien. Un engagement simple et utile.",
    image: ImgChatonRoux,
    href: "https://www.teaming.net/associationsavoinimaux-protectionanimale",
  },
  {
    title: "Matériel & Alimentaire",
    description:
      "Pâtées, croquettes, compléments, traitements, cages, laisses, harnais, jouets, couvertures…",
    image: ImgDons,
    href: "https://www.facebook.com/BoutiqueSolidaireSavoinimaux/about/?ref=page_internal",
  },
  {
    title: "Boutique Solidaire",
    description:
      "Soutenez l’association autrement grâce à la boutique solidaire et aux achats utiles au refuge.",
    image: ImgBoutiqueSolidaire,
    href: "https://www.facebook.com/BoutiqueSolidaireSavoinimaux/about/?ref=page_internal",
  },
  {
    title: "Adhésion",
    description:
      "Rejoignez officiellement l’association et participez à son action dans la durée.",
    image: ImgChatSiamois,
    type: "adhesion-modal",
  },
  {
    title: "Rejoindre la Team",
    description:
      "Bénévolat, logistique, famille d'accueil, événements, communication ou terrain : chacun peut trouver sa place.",
    image:
      "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=1200&q=80",
    href: "#contact-form",
  },
  
];

  useEffect(() => {
    const interval = setInterval(() => {
      setStoryIndex((prev) => (prev + 1) % stories.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [stories.length]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        heroTitleRef.current,
        { opacity: 0, y: isMobile ? 24 : 42, filter: "blur(10px)" },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 1,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        heroTextRef.current,
        { opacity: 0, y: isMobile ? 16 : 26, filter: "blur(8px)" },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.9,
          delay: 0.12,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        heroActionsRef.current,
        { opacity: 0, y: isMobile ? 12 : 18 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: 0.22,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        heroVisualRef.current,
        { opacity: 0, scale: isMobile ? 0.985 : 0.94, y: isMobile ? 12 : 20 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 1.05,
          delay: 0.12,
          ease: "power3.out",
        }
      );

      gsap.to(bgGlow1Ref.current, {
        x: 40,
        y: -30,
        duration: 9,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(bgGlow2Ref.current, {
        x: -45,
        y: 35,
        duration: 11,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(bgGlow3Ref.current, {
        x: 25,
        y: 20,
        duration: 13,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      if (pawTrailRef.current && !isMobile) {
        gsap.to(pawTrailRef.current, {
          y: -30,
          rotate: 2,
          opacity: 0.9,
          duration: 8,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      }

      if (!isMobile) {
        gsap.to(heroVisualRef.current, {
          y: 18,
          duration: 4.5,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });

        sectionRefs.current.forEach((section) => {
          if (!section) return;

          gsap.fromTo(
            section,
            { opacity: 0, y: 70, filter: "blur(10px)" },
            {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              duration: 1.1,
              ease: "power3.out",
              scrollTrigger: {
                trigger: section,
                start: "top 80%",
              },
            }
          );
        });
      }
    }, containerRef);

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      ctx.revert();
    };
  }, [isMobile]);

  const nextStory = () => {
    setStoryIndex((prev) => (prev + 1) % stories.length);
  };

  const prevStory = () => {
    setStoryIndex((prev) => (prev - 1 + stories.length) % stories.length);
  };

  const currentStory = stories[storyIndex];

  const partnersLogos = [
    {
      name: "Médor et Compagnie",
      image: "https://static.wixstatic.com/media/456cbd_64bc168788a647fda476e0daf7e5597d~mv2.png/v1/fill/w_316,h_370,al_c,lg_1,q_85,enc_avif,quality_auto/image_edited.png",
      siteWeb: "https://www.medoretcie.com/animalerie/medor-et-compagnie-chambery-73000-chr.html?srsltid=AfmBOoq2JTfCsHQsI-fNtDz_1srJYnDkatxKng95WbGTAwiQOISiiguO",
    },
    {
      name: "Carrefour Market",
      image: "https://static.wixstatic.com/media/456cbd_1e604860944749b5971c2110ca15da05~mv2.jpg/v1/fill/w_572,h_670,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/383666_carrefour-market.jpg",
      siteWeb: "https://www.carrefour.fr/enseignes/market",
    },
    {
      name: "Hyper U",
      image: "https://static.wixstatic.com/media/456cbd_12100f57b89249cb8bc8791ce94eb927~mv2.png/v1/fill/w_427,h_500,al_c,q_85,enc_avif,quality_auto/image.png",
    },
    {
      name: "Cyno-Pro",
      image: "https://static.wixstatic.com/media/456cbd_6ed2a382c6cf4728b9938ca4cb0e2879~mv2.png/v1/fill/w_269,h_315,al_c,lg_1,q_85,enc_avif,quality_auto/image.png",
      siteWeb: "https://cyno-pro.com/magasins",
    },
    {
      name: "Gamm Vert",
      image: "https://static.wixstatic.com/media/456cbd_c245886e1461465d8c5ee792605c3fc3~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/image.png",
      siteWeb: "https://cyno-pro.com/magasins",
    },
    {
      name: "Bricomarché",
      image: "https://static.wixstatic.com/media/456cbd_4fb3bf112ffc41bf9e8a16686dc7b28e~mv2.png/v1/fill/w_410,h_481,al_c,lg_1,q_85,enc_avif,quality_auto/image_edited_edited.png",
    },
    {
      name: "Clinique NAC & Compagnie",
      image: "https://static.wixstatic.com/media/456cbd_bee67cbf91334c0c9463efd5f030f37a~mv2.png/v1/fill/w_461,h_540,al_c,lg_1,q_85,enc_avif,quality_auto/Nacscie-e1716017556880_edited.png",
      siteWeb: "https://www.nacetcompagnie.fr",
    },
    {
      name: "Clinique vétérinaire Mont des Princes",
      image: "https://static.wixstatic.com/media/456cbd_cc6b8de2310644a1948826c664db399e~mv2.png/v1/fill/w_360,h_421,al_c,lg_1,q_85,enc_avif,quality_auto/image_edited.png",
      siteWeb: "http://www.vetomontdesprinces.com",
    },
    {
      name: "Clinique vétérinaire de l'Albanais",
      image: "https://static.wixstatic.com/media/456cbd_785f7b4192904861a9267c4fb122f5d4~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/image_edited_edited.png",
      siteWeb: "https://veterinaire-albanais.com",
    },
    {
      name: "La Yourte Vétérinaire",
      image: "https://static.wixstatic.com/media/456cbd_1bef74b510204e44a6a77a5502e8c3d2~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/image_edited_edited.png",
      siteWeb: "http://yourteveterinaire.com",
    },
    {
      name: "Clinique vétérinaire de la Tournette",
      image: "https://static.wixstatic.com/media/456cbd_273dfbbc03be429089966858ed7996b8~mv2.png/v1/fill/w_526,h_616,al_c,lg_1,q_85,enc_avif,quality_auto/CVTournette_edited.png",
      siteWeb: "https://cliniquedelatournette.myvetapps.fr/index.php/page/home",
    },
    {
      name: "Dr Senger Edouard",
      image: "https://static.wixstatic.com/media/456cbd_f485fb481d5c41678c07e081092e32f5~mv2.webp/v1/fill/w_572,h_670,al_c,lg_1,q_85,enc_avif,quality_auto/Tipaw-Connect-content-10-1618416120967-full-314390.webp",
      siteWeb: "https://vetissimo.fr",
    },
    {
      name: "Cabinet vétérinaire Porte des Bauges",
      image: "https://static.wixstatic.com/media/456cbd_7d4c88e8b1b64ac7bbc39bd1bdef9aec~mv2.png/v1/fill/w_430,h_504,al_c,q_85,enc_avif,quality_auto/15a85c1950293cd40ccc563e44fb1b4040cf7181.png",
      siteWeb: "https://share.google/HGjebijFskUoda0qr",
    },
    {
      name: "Clinique vétérinaire VetoLac",
      image: "https://static.wixstatic.com/media/456cbd_1d3593d5beb644a1b0d6168d61198039~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/vetolac_edited.png",
      siteWeb: "https://www.argos-veterinaire.com/nos-cliniques-veterinaires-2/clinique/vetolac-237",
    },
    {
      name: "Clinique vétérinaire VetAfforêts",
      image: "https://static.wixstatic.com/media/456cbd_e7850527493346ebbe692893da056944~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/Fiche-Presentation-Logo-LES-AFFORETS-300.png",
      siteWeb: "https://www.vetafforets.fr",
    },
    {
      name: "Centre Hospitalier Vétérinaire Saint-Martin",
      image: "https://static.wixstatic.com/media/456cbd_dddd88e09ecd4786b2c289b9ff0237ec~mv2.jpg/v1/fill/w_512,h_600,al_c,lg_1,q_80,enc_avif,quality_auto/637e3b484e0000f059544e1e.jpg",
      siteWeb: "https://chvsm.com",
    },
    {
      name: "Clinique vétérinaire de l'Épine",
      image: "https://static.wixstatic.com/media/456cbd_f159476464794a2d8ece8a75656521dc~mv2.png/v1/fill/w_252,h_295,al_c,lg_1,q_85,enc_avif,quality_auto/6655a4b32e0000ca1529f555_edited.png",
      siteWeb: "https://www.veterinaire-monveto.com/cliniques/clinique-veterinaire-de-lepine/?utm_source=gmb",
    },
    {
      name: "Clinique vétérinaire du Grand Verger",
      image: "https://static.wixstatic.com/media/456cbd_07fb5e1fb2924b2f98d6467e3b5b7682~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/62860f7ade614329761147_edited.png",
      siteWeb: "https://www.cliniqueveterinairedugrandverger.com",
    },
    {
      name: "Hardcore Care",
      image: "https://static.wixstatic.com/media/456cbd_16973ca070dd4b9686068915ef13ac14~mv2.png/v1/fill/w_400,h_468,al_c,lg_1,q_85,enc_avif,quality_auto/image_edited.png",
    },
    {
      name: "Fondation Brigitte Bardot",
      image: "https://static.wixstatic.com/media/456cbd_b562e2876d654d279fd35f2b1624d250~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/fondation-brigitte-bardot-logo_edited_pn.png",
    },
    {
      name: "FMR Radio",
      image: "https://static.wixstatic.com/media/456cbd_1c7795a8f78349d28b4c0d777927c57c~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/image_edited.png",
    },
    {
      name: "Un geste un sourire",
      image: "https://static.wixstatic.com/media/456cbd_8e5b223c0891492d9059b4d5ab46ea26~mv2.png/v1/fill/w_572,h_670,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/image_edited.png",
    },
    {
      name: "Clinique Vétérinaire République",
      image: "https://www.monrendezvousveto.fr/media/cache/clinic_thumb/uploads/images/clinic/63/63ecea97a2fbb878363789.png"
    },
    {
      name: "Clinique vétérinaire Arvets",
      image: "https://arvets.fr/wp-content/uploads/2024/02/logo-clinique-arvets.webp",
    }
  ];

  return (
    <div
      ref={containerRef}
      className="relative isolate min-h-screen overflow-x-hidden bg-[#f8f5f0] text-[#111111]"
    >
      <Helmet>
        <title>Savoinimaux | Association de protection animale en Savoie et Haute-Savoie</title>
        <meta
          name="description"
          content="Savoinimaux est une association de protection animale en Savoie et Haute-Savoie. Sauvetage, soins, accueil, accompagnement et adoption pour offrir une seconde chance aux animaux abandonnés ou en danger."
        />
        <link rel="canonical" href="https://savoinimaux.com/" />

        <meta
          name="keywords"
          content="Savoinimaux, protection animale Savoie, protection animale Haute-Savoie, association animale Rumilly, refuge animaux Savoie, refuge animaux Haute-Savoie, adoption animaux Savoie, adoption animaux Haute-Savoie, sauvetage animaux Pays de Savoie"
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Savoinimaux | Association de protection animale en Savoie et Haute-Savoie"
        />
        <meta
          property="og:description"
          content="Découvrez Savoinimaux, association engagée pour le sauvetage, les soins, l’accueil et l’adoption des animaux en Savoie et Haute-Savoie."
        />
        <meta property="og:url" content="https://savoinimaux.com/" />
        <meta
          property="og:image"
          content="https://savoinimaux.com/og-image.jpg"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Savoinimaux | Association de protection animale en Savoie et Haute-Savoie"
        />
        <meta
          name="twitter:description"
          content="Association dédiée au sauvetage, aux soins et à l’adoption des animaux en Savoie et Haute-Savoie."
        />
        <meta
          name="twitter:image"
          content="https://savoinimaux.com/og-image.jpg"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "NGO",
                "@id": "https://savoinimaux.com/#organization",
                name: "Savoinimaux",
                url: "https://savoinimaux.com/",
                logo: "https://savoinimaux.com/logo-512.png",
                image: "https://savoinimaux.com/og-image.jpg",
                description:
                  "Savoinimaux | Association de protection animale en Savoie et Haute-Savoie. Sauvetage, soins, accueil et adoption des animaux abandonnés ou en danger.",
                telephone: "+33 6 44 68 52 53",
                email: "savoinimaux@gmail.com",
                sameAs: [
                  "https://www.facebook.com/Savoinimaux/",
                  "https://www.instagram.com/savoinimaux/",
                ],
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "6 rue filaterie",
                  postalCode: "74150",
                  addressLocality: "Rumilly",
                  addressCountry: "FR",
                },
                areaServed: [
                  {
                    "@type": "AdministrativeArea",
                    name: "Savoie",
                  },
                  {
                    "@type": "AdministrativeArea",
                    name: "Haute-Savoie",
                  },
                ],
              },
              {
                "@type": "WebSite",
                "@id": "https://savoinimaux.com/#website",
                url: "https://savoinimaux.com/",
                name: "Savoinimaux",
                publisher: {
                  "@id": "https://savoinimaux.com/#organization",
                },
                inLanguage: "fr-FR",
              },
              {
                "@type": "WebPage",
                "@id": "https://savoinimaux.com/#webpage",
                url: "https://savoinimaux.com/",
                name: "Savoinimaux | Association de protection animale en Savoie et Haute-Savoie",
                isPartOf: {
                  "@id": "https://savoinimaux.com/#website",
                },
                about: {
                  "@id": "https://savoinimaux.com/#organization",
                },
                description:
                  "Savoinimaux agit en Savoie et Haute-Savoie pour protéger, soigner et faire adopter les animaux abandonnés.",
                inLanguage: "fr-FR",
              },
            ],
          })}
        </script>
      </Helmet>
      {/* BACKGROUND PREMIUM V2 */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        {/* Base fond ivoire profond */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,#fcfaf7_0%,#f8f4ee_34%,#f4efe8_68%,#f1ebe3_100%)]" />

        {/* Halo principal haut gauche */}
        <div
          ref={bgGlow1Ref}
          className="absolute left-[-180px] top-[-140px] h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(224,60,49,0.14)_0%,rgba(224,60,49,0.08)_26%,rgba(224,60,49,0.03)_48%,transparent_72%)] blur-[60px]"
        />

        {/* Halo secondaire droite */}
        <div
          ref={bgGlow2Ref}
          className="absolute right-[-220px] top-[8%] h-[720px] w-[720px] rounded-full bg-[radial-gradient(circle,rgba(210,160,120,0.14)_0%,rgba(210,160,120,0.08)_28%,rgba(210,160,120,0.03)_50%,transparent_72%)] blur-[70px]"
        />

        {/* Halo bas centre */}
        <div
          ref={bgGlow3Ref}
          className="absolute bottom-[-260px] left-1/2 h-[760px] w-[760px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(120,100,80,0.09)_0%,rgba(120,100,80,0.04)_36%,transparent_72%)] blur-[75px]"
        />

        {/* Lumière éditoriale centrale */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(255,255,255,0.82)_0%,rgba(255,255,255,0.35)_24%,transparent_56%)]" />

        {/* Vignette douce pour donner profondeur */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_38%,rgba(0,0,0,0.035)_100%)]" />

        {/* Grain très fin */}
        <div className="absolute inset-0 opacity-[0.045] mix-blend-multiply [background-image:radial-gradient(rgba(0,0,0,0.9)_0.6px,transparent_0.6px)] [background-size:8px_8px]" />

        {/* Trame ultra légère */}
        <div className="absolute inset-0 opacity-[0.03] [background-image:linear-gradient(to_right,rgba(0,0,0,0.16)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.16)_1px,transparent_1px)] [background-size:96px_96px]" />

        {/* Balayage lumineux subtil */}
        <div className="absolute left-[-20%] top-0 h-full w-[55%] rotate-[8deg] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.18),transparent)] blur-3xl opacity-70" />

        {/* Paw prints fantomatiques */}
        <div className="absolute left-[7%] top-[14%] text-[#d9cfc3]/45">
          <PawPrint className="h-14 w-14 rotate-[-18deg]" />
        </div>
        <div className="absolute left-[11%] top-[23%] text-[#e4dbd0]/35">
          <PawPrint className="h-10 w-10 rotate-[8deg]" />
        </div>
        <div className="absolute right-[10%] top-[28%] text-[#d7ccc0]/35">
          <PawPrint className="h-12 w-12 rotate-[16deg]" />
        </div>
        <div className="absolute right-[14%] bottom-[18%] text-[#d8cec3]/30">
          <PawPrint className="h-14 w-14 rotate-[-12deg]" />
        </div>
        <div className="absolute left-[16%] bottom-[12%] text-[#ddd4c9]/28">
          <PawPrint className="h-11 w-11 rotate-[10deg]" />
        </div>
      </div>

      {!isMobile && (
        <div
          ref={pawTrailRef}
          className="pointer-events-none absolute right-[5%] top-[160px] -z-[1] hidden xl:block"
        >
          <div className="relative h-[720px] w-[240px] opacity-30">
            <PawPrint className="absolute left-14 top-0 h-10 w-10 rotate-[-18deg] text-red-300" />
            <PawPrint className="absolute left-0 top-24 h-8 w-8 rotate-[12deg] text-black/20" />
            <PawPrint className="absolute left-20 top-52 h-9 w-9 rotate-[18deg] text-red-200" />
            <PawPrint className="absolute left-8 top-80 h-7 w-7 rotate-[-10deg] text-black/15" />
            <PawPrint className="absolute left-24 top-[430px] h-10 w-10 rotate-[14deg] text-red-300/80" />
            <PawPrint className="absolute left-12 top-[570px] h-8 w-8 rotate-[-8deg] text-black/15" />
          </div>
        </div>
      )}

      {!isMobile && (
        <>
          <div className="pointer-events-none absolute left-[7%] top-[24%] -z-[2] hidden xl:block text-red-100/80">
            <Sparkles className="h-16 w-16" />
          </div>
          <div className="pointer-events-none absolute left-[10%] bottom-[18%] -z-[2] hidden xl:block text-black/[0.05]">
            <PawPrint className="h-20 w-20 rotate-[-14deg]" />
          </div>
          <div className="pointer-events-none absolute right-[11%] top-[42%] -z-[2] hidden xl:block text-red-100/70">
            <Sparkles className="h-12 w-12" />
          </div>
        </>
      )}

      <main>
        <section className="relative">
          <div className="mx-auto grid min-h-[88vh] max-w-7xl items-center gap-10 px-6 py-14 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:py-20">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-white/85 px-4 py-2 text-[11px] uppercase tracking-[0.32em] text-red-600 shadow-[0_10px_30px_rgba(220,38,38,0.08)] backdrop-blur-xl">
                <span className="h-2 w-2 rounded-full bg-red-600" />
                Association de protection animale
              </div>

              <div ref={heroTitleRef} className="relative mt-6 max-w-4xl">
                <h1 className="text-5xl font-semibold leading-[0.92] text-[#111111] md:text-7xl xl:text-7xl">
                  Ils n&apos;ont rien demandé.
                  <span className="block bg-gradient-to-r from-red-600 via-red-500 to-[#ff6b57] bg-clip-text text-transparent">
                    Mais ils ont tout perdu.
                  </span>
                </h1>

                {!isMobile && (
                  <img
                    src={clippath}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="pointer-events-none absolute left-[58%] top-[-22px] w-[88px] select-none object-contain"
                  />
                )}
              </div>

              <p
                ref={heroTextRef}
                className="mt-8 max-w-2xl text-base leading-8 text-black/65 md:text-lg"
              >
                Savoinimaux protège, soigne et accompagne les animaux abandonnés,
                perdus ou en danger en Savoie et Haute-Savoie. L’association agit
                chaque jour pour leur offrir une seconde chance, grâce aux familles
                d’accueil, aux bénévoles, aux partenaires et aux adoptants.
              </p>

              <div
                ref={heroActionsRef}
                className="mt-10 flex flex-col gap-4 sm:flex-row"
              >
                <a
                  href="#engagement"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-red-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_18px_55px_rgba(220,38,38,0.22)] transition duration-300 hover:translate-y-[-2px] hover:shadow-[0_24px_70px_rgba(220,38,38,0.26)]"
                >
                  Faire un don
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#adoption"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white/85 px-7 py-4 text-sm font-semibold text-[#111111] shadow-[0_8px_30px_rgba(0,0,0,0.04)] backdrop-blur-xl transition hover:border-red-200 hover:bg-red-50/80"
                >
                  Suivre nos animaux sur les réseaux
                </a>
              </div>
            </div>

            <div ref={heroVisualRef} className="relative order-1 lg:order-2">
              {!isMobile && (
                <>
                  <div className="absolute -left-10 top-10 hidden h-44 w-44 rounded-full bg-red-100/70 blur-3xl xl:block" />
                  <div className="absolute -right-6 bottom-12 hidden h-56 w-56 rounded-full bg-black/5 blur-3xl xl:block" />
                  <div className="absolute left-[-18px] top-20 hidden text-red-100 xl:block">
                    <PawPrint className="h-20 w-20 rotate-[-14deg]" />
                  </div>
                  <div className="absolute right-[-10px] top-12 hidden text-black/[0.05] xl:block">
                    <Sparkles className="h-14 w-14" />
                  </div>
                </>
              )}

                <div className="relative min-h-[520px] overflow-hidden rounded-[2.3rem] border border-white/60 bg-white/80 shadow-[0_25px_90px_rgba(0,0,0,0.09)] backdrop-blur-xl md:h-[560px]">
                  <div className="absolute inset-0 rounded-[2.3rem] bg-gradient-to-br from-white/45 via-transparent to-red-50/30 pointer-events-none z-10" />

                  <div className="absolute inset-0 rounded-[2.3rem] overflow-hidden">
                    {heroImages.map((image, index) => (
                      <img
                        key={image}
                        src={image}
                        alt="Animal pris en charge par Savoinimaux"
                        loading={index === 0 ? "eager" : "lazy"}
                        className={`absolute inset-0 h-full w-full object-cover rounded-[2.3rem] will-change-transform transition-all duration-1000 ease-out ${
                          index === heroImageIndex
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-[1.02]"
                        }`}
                      />
                    ))}
                  </div>

                  <div className="absolute inset-0 rounded-[2.3rem] bg-gradient-to-t from-black/50 via-black/5 to-transparent z-10" />
                  <div className="absolute inset-0 rounded-[2.3rem] bg-gradient-to-r from-red-600/10 via-transparent to-transparent z-10" />

                  <div className="absolute left-4 right-4 top-4 z-20 rounded-full border border-white/30 bg-white/80 px-4 py-2 text-center text-[10px] uppercase leading-relaxed tracking-[0.18em] text-[#111111] shadow-[0_8px_25px_rgba(0,0,0,0.05)] backdrop-blur-md sm:left-6 sm:right-auto sm:top-6 sm:text-[11px] sm:tracking-[0.28em]">
                    Sauver. Soigner. Protéger.
                  </div>

                

                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                  <div className="max-w-lg rounded-[1.8rem] border border-white/25 bg-black/35 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.08)] backdrop-blur-xl sm:p-6">
                    <p className="text-xs uppercase tracking-[0.34em]  text-white/70">
                      Une mission concrète
                    </p>
                    <h2 className="mt-3 text-[clamp(1.35rem,6vw,1.875rem)] font-semibold leading-tight text-white">
                      Chaque animal mérite une seconde chance.
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-white/85 [overflow-wrap:anywhere]">
                      Derrière chaque prise en charge, il y a une histoire, une
                      urgence et un engagement humain. Savoinimaux agit chaque
                      jour pour protéger, soigner et accompagner des animaux
                      abandonnés ou en difficulté, avec l’objectif de leur offrir
                      un avenir plus stable et plus serein.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div id="mission" ref={(el) => (sectionRefs.current[0] = el)}>
          <OurMissionSection />
        </div>

        <ImpactStatsSection />

        <section
          id="impact"
          ref={(el) => (sectionRefs.current[1] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr]">
              <div className="relative overflow-hidden rounded-[2.2rem] border border-white/50 bg-white/80 p-3 shadow-[0_24px_80px_rgba(0,0,0,0.05)] backdrop-blur-xl">
                <img
                  src={ImgRouxDuo}
                  alt="Main en contact avec une patte"
                  loading="lazy"
                  className="h-full min-h-[420px] w-full rounded-[1.7rem] object-cover"
                />
                <div className="absolute inset-3 rounded-[1.7rem] bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8 max-w-md">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/60">
                    Notre engagement
                  </p>
                  <h3 className="mt-3 text-3xl font-semibold text-white">
                    Des gestes concrets, chaque semaine.
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-white/80">
                    Soins, accueils, accompagnements, sensibilisation : derrière
                    chaque chiffre, il y a des histoires, du temps, et un
                    véritable engagement humain.
                  </p>
                </div>
              </div>

              <div className="flex flex-col justify-center rounded-[2.2rem] border border-white/50 bg-white/80 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.04)] backdrop-blur-xl md:p-10">
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Pourquoi agir
                </p>

                <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#111111] md:text-5xl">
                  Une réalité locale,
                  <span className="block bg-gradient-to-r from-red-600 to-[#ff7a59] bg-clip-text text-transparent">
                    et des besoins concrets.
                  </span>
                </h2>

                <p className="mt-6 max-w-2xl text-base leading-8 text-black/62">
                  Chaque année, de nombreux animaux sont abandonnés, perdus ou
                  laissés sans soins. Derrière ces situations, il y a souvent
                  l’urgence, l’isolement ou un manque de moyens. Savoinimaux
                  intervient pour apporter des solutions concrètes, rapidement et
                  efficacement.
                </p>

                <p className="mt-4 max-w-2xl text-base leading-8 text-black/62">
                  L’association agit en lien avec des familles d’accueil, des
                  vétérinaires et des partenaires locaux afin d’assurer une prise
                  en charge complète : du sauvetage jusqu’à l’adoption. Chaque
                  intervention est pensée pour offrir une solution durable à
                  l’animal.
                </p>

                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                  {[
                    "Interventions locales en Savoie",
                    "Suivi des animaux sur le long terme",
                    "Accompagnement des adoptants",
                    "Mobilisation d’un réseau solidaire",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-black/6 bg-[#faf9f7] px-5 py-4 text-sm text-black/66 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="partners"
          ref={(el) => (sectionRefs.current[2] = el)}
          className="relative py-14"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mb-8 flex items-end justify-between gap-6">
              <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Ils soutiennent l’association
                </p>
                <h2 className="mt-3 text-3xl font-semibold text-[#111111] md:text-4xl">
                  Un réseau de partenaires engagés aux côtés de Savoinimaux
                </h2>
                <p className="mt-4 text-base leading-8 text-black/62">
                  Cliniques vétérinaires, commerces, structures locales et partenaires solidaires
                  participent concrètement à la protection animale sur le territoire.
                </p>
              </div>

              <a
                href="/partenaires"
                className="hidden text-sm font-semibold text-red-600 transition hover:text-red-500 md:inline-flex"
              >
                Voir la page partenaires
              </a>
            </div>

            <div className="relative overflow-hidden rounded-[2rem] py-6 ">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[#F9F4F1] to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[#F9F4F1] to-transparent" />

              <div
                className="flex w-max gap-5 px-6 partners-marquee">
                {[...partnersLogos, ...partnersLogos].map((partner, index) => {
                  const content = (
                    <div className="flex h-[110px] w-[190px] items-center justify-center rounded-[1.4rem] border border-black/8 bg-white px-1 py-1 shadow-[0_8px_24px_rgba(0,0,0,0.03)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_14px_35px_rgba(0,0,0,0.06)]">
                      <img
                        src={partner.image}
                        alt={partner.name}
                        className="max-h-[88px] max-w-full object-contain grayscale-[0.08] transition duration-300 hover:grayscale-0"
                        loading="lazy"
                      />
                    </div>
                  );

                  return partner.siteWeb ? (
                    <a
                      key={`${partner.name}-${index}`}
                      href={partner.siteWeb}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={partner.name}
                      title={partner.name}
                    >
                      {content}
                    </a>
                  ) : (
                    <div
                      key={`${partner.name}-${index}`}
                      aria-label={partner.name}
                      title={partner.name}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>

            <a
              href="/partenaires"
              className="mt-6 inline-flex text-sm font-semibold text-red-600 transition hover:text-red-500 md:hidden"
            >
              Voir la page partenaires
            </a>
          </div>
        </section>

        <section
          id="stories"
          ref={(el) => (sectionRefs.current[3] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mb-12 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Histoires coup de cœur
                </p>
                <h2 className="mt-4 text-4xl font-semibold text-[#111111] md:text-6xl">
                  Ils ont connu l’abandon,
                  <span className="block bg-gradient-to-r from-red-600 to-[#ff7a59] bg-clip-text text-transparent">
                    ils découvrent une seconde chance.
                  </span>
                </h2>

                <p className="mt-5 text-base leading-8 text-black/62">
                  Chaque animal pris en charge a une histoire. Derrière chaque
                  regard, il y a un passé, souvent difficile, et un chemin vers
                  une nouvelle vie. Ces histoires témoignent du travail réalisé
                  par l’association et de l’impact concret de chaque
                  intervention.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={prevStory}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[#111111] shadow-sm backdrop-blur-xl transition hover:border-red-200 hover:bg-red-50"
                  aria-label="Histoire précédente"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={nextStory}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[#111111] shadow-sm backdrop-blur-xl transition hover:border-red-200 hover:bg-red-50"
                  aria-label="Histoire suivante"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="overflow-hidden rounded-[2.2rem] border border-white/50 bg-white/80 shadow-[0_24px_80px_rgba(0,0,0,0.05)] backdrop-blur-xl">
                <img
                  key={currentStory.image}
                  src={currentStory.image}
                  alt={currentStory.name}
                  loading="lazy"
                  className="h-96 w-full object-cover md:h-[470px]"
                />
              </div>

              <div className="flex flex-col justify-center rounded-[2.2rem] border border-white/50 bg-white/80 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.04)] backdrop-blur-xl md:p-10">
                <div className="flex items-center gap-3 text-red-600">
                  <Quote className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-[0.34em]">
                    Histoire {storyIndex + 1} / {stories.length}
                  </span>
                </div>

                <h3 className="mt-5 text-4xl font-semibold text-[#111111] md:text-5xl">
                  {currentStory.name}
                </h3>
                <p className="mt-4 text-xl leading-9 text-[#1c1c1c]">
                  {currentStory.title}
                </p>
                <p className="mt-6 text-base leading-8 text-black/62">
                  {currentStory.text}
                </p>

                <div className="mt-10 flex items-center gap-3">
                  {stories.map((story, index) => (
                    <button
                      key={story.name}
                      onClick={() => setStoryIndex(index)}
                      className={`h-2.5 rounded-full transition ${
                        index === storyIndex
                          ? "w-10 bg-red-600"
                          : "w-2.5 bg-black/10"
                      }`}
                      aria-label={`Afficher l'histoire ${story.name}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

       <section
  id="adoption"
  ref={(el) => (sectionRefs.current[4] = el)}
  className="relative py-24"
>
  <div className="mx-auto max-w-7xl px-6 lg:px-10">
    <div className="mx-auto mb-7 max-w-4xl text-center">
      <p className="text-xs uppercase tracking-[0.35em] text-black/38">
        Animaux à adopter
      </p>

      <h2 className="mt-4 text-4xl font-semibold text-[#111111] md:text-6xl">
        Adoption
        <span className="block bg-gradient-to-r from-red-600 to-[#ff7a59] bg-clip-text text-transparent">
          Donnez-leur une nouvelle vie.
        </span>
      </h2>

      <p className="mt-6 text-base leading-8 text-black/62">
        Une sélection de chats à l’adoption est présentée directement sur le site.
        <br className="hidden md:block" />
        <span className="font-medium text-[#111111]">
          Les chatons, les urgences et les nouvelles arrivées sont publiés uniquement sur Facebook et Instagram
        </span>{" "}
        afin de partager les informations plus rapidement et toucher un maximum de personnes.
      </p>
    </div>

    <ScrollingBanner />

    <div className="mt-14 grid gap-6 md:grid-cols-2">
      <div className="rounded-[2rem] border border-white/50 bg-white/80 p-8 shadow-[0_20px_70px_rgba(0,0,0,0.04)] backdrop-blur-xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f5f5f5] text-[#111111]">
            <FacebookIcon className="h-10 w-10 text-[#1877F2]" />
          </div>
          <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
            Mises à jour régulières
          </span>
        </div>

        <h3 className="mt-6 text-3xl font-semibold text-[#111111]">
          Facebook
        </h3>

        <p className="mt-4 text-base leading-8 text-black/60">
          Retrouvez les chatons, les urgences, les sauvetages récents et les
          publications d’adoption directement sur la page Facebook de
          l’association.
        </p>

        <a
          href="https://www.facebook.com/Savoinimaux/"
          target="_blank"
          rel="noreferrer"
          aria-label="Facebook de Savoinimaux"
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition hover:text-red-500"
        >
          Voir Facebook
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>

      <div className="rounded-[2rem] border border-white/50 bg-white/80 p-8 shadow-[0_20px_70px_rgba(0,0,0,0.04)] backdrop-blur-xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f5f5f5] text-[#111111]">
            <InstagramIcon className="h-10 w-10 text-pink-600" />
          </div>
          <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
            Mises à jour régulières
          </span>
        </div>

        <h3 className="mt-6 text-3xl font-semibold text-[#111111]">
          Instagram
        </h3>

        <p className="mt-4 text-base leading-8 text-black/60">
          Suivez les histoires du quotidien, les nouveaux animaux pris en charge,
          les adoptions réussies et les coulisses de l’association.
        </p>

        <a
          href="https://www.instagram.com/savoinimaux/"
          target="_blank"
          rel="noreferrer"
          aria-label="Instagram de Savoinimaux"
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition hover:text-red-500"
        >
          Voir Instagram
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>

    {cats.length > 0 && (
      <div className="mt-20">
        <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-black/38">
              À l’adoption
            </p>
            <h3 className="mt-3 text-3xl font-semibold text-[#111111] md:text-5xl">
              Quelques chats à rencontrer
            </h3>
          </div>

          <a
            href="/adoption"
            className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition hover:text-red-500"
          >
            Voir tous les chats
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

<div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
  {cats.slice(0, 3).map((cat) => (
    <Link
      key={cat.slug || cat.name}
      to={`/adoption/${cat.slug}`}
      className="group relative block h-full"
    >
      <article className="relative flex h-full flex-col overflow-hidden rounded-[2.3rem] border border-white/70 bg-white/85 p-3 shadow-[0_24px_80px_rgba(0,0,0,0.06)] backdrop-blur-xl transition duration-500 hover:-translate-y-2 hover:shadow-[0_38px_110px_rgba(220,38,38,0.15)]">
        <div className="relative h-[330px] overflow-hidden rounded-[1.9rem]">
          <img
            src={cat.image}
            alt={cat.name}
            loading="lazy"
            className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.055]"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 via-transparent to-transparent opacity-0 transition duration-500 group-hover:opacity-100" />

          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            {cat.urgent && (
              <span className="rounded-full bg-red-600 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-lg">
                Urgent
              </span>
            )}

            <span className="rounded-full border border-white/30 bg-white/85 px-4 py-2 text-xs font-semibold text-red-600 backdrop-blur-md">
              {cat.status || "À adopter"}
            </span>
          </div>

          <div className="absolute bottom-5 left-5 right-5">
            <p className="text-xs uppercase tracking-[0.28em] text-white/70">
              {cat.race || "Européen"}
            </p>

            <h4 className="mt-2 text-4xl font-semibold leading-none text-white">
              {cat.name}
            </h4>
          </div>
        </div>

        <div className="flex flex-1 flex-col px-3 pb-5 pt-6">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
              {cat.age || cat.ageLabel}
            </span>

            <span className="rounded-full bg-black/[0.04] px-3 py-1 text-xs font-semibold text-black/55">
              {cat.sex}
            </span>

            {cat.fiv && (
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                FIV+
              </span>
            )}
          </div>

          <p className="mt-4 flex-1 text-sm leading-7 text-black/60">
            {cat.description || cat.shortDescription}
          </p>

          <div className="mt-6 flex items-center justify-between border-t border-black/8 pt-5">
            <span className="text-sm font-semibold text-red-600">
              Voir sa fiche
            </span>

            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white shadow-[0_12px_30px_rgba(220,38,38,0.22)] transition duration-300 group-hover:translate-x-1">
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  ))}
</div>
      </div>
    )}
  </div>
</section>

        <section
          id="engagement"
          ref={(el) => (sectionRefs.current[5] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="relative overflow-hidden p-8 md:p-12">
              <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-red-100/70 blur-3xl" />
              <div className="absolute bottom-0 left-0 h-44 w-44 rounded-full bg-[#f6d9d6]/40 blur-3xl" />
              <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,rgba(0,0,0,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.12)_1px,transparent_1px)] [background-size:88px_88px]" />

              {!isMobile && (
                <>
                  <div className="absolute right-8 top-8 hidden text-red-100 lg:block">
                    <PawPrint className="h-16 w-16 rotate-[18deg]" />
                  </div>
                  <div className="absolute right-16 bottom-10 hidden text-red-100/70 lg:block">
                    <Sparkles className="h-12 w-12" />
                  </div>
                </>
              )}

            
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-black/45">
                    Comment aider
                  </p>

                  <h2 className="mt-4 text-4xl font-semibold leading-[0.95] text-[#111111] md:text-6xl">
                    Ensemble, changeons
                    <span className="block">leur destin.</span>
                  </h2>

                  <p className="mt-8  text-base leading-8 text-black/68 md:text-[1.05rem]">
                    Soutenir Savoinimaux, ce n’est pas seulement faire un don. C’est aussi
                    permettre à l’association de continuer ses prises en charge, ses suivis
                    vétérinaires, ses accueils d’urgence et son travail quotidien auprès
                    d’animaux fragilisés, abandonnés ou en attente d’un nouveau départ.
                  </p>

                  <p className="mt-5  text-base leading-8 text-black/68 md:text-[1.05rem]">
                    Chaque participation compte. Un partage sur les réseaux peut aider à
                    trouver une famille. Une famille d’accueil peut offrir un sas
                    indispensable à un animal en reconstruction. Un bénévole peut faire
                    gagner un temps précieux sur le terrain. Et un don peut déclencher une
                    intervention au bon moment.
                  </p>

                </div>

     

             
                <div className="grid gap-10 mt-10 md:grid-cols-2 xl:grid-cols-3">
                  {helpingOptions.map((item) => {
                    const cardContent = (
                      <div className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-red-100/80 bg-[linear-gradient(135deg,rgba(220,38,38,0.07),rgba(255,255,255,0.96),rgba(255,248,248,0.92))] backdrop-blur-xl transition duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_80px_rgba(221,65,47,0.12)]">
                        {/* Image */}
                        <div className="relative px-5 pt-5">
                          <div className="relative h-[260px] overflow-hidden rounded-[1.65rem]">
                            <img
                              src={item.image}
                              alt={item.title}
                              loading="lazy"
                              className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.045]"
                            />

                            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.02)_0%,rgba(0,0,0,0.08)_100%)]" />
                          </div>

                          <div className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2">
                            <span className="inline-flex min-w-[190px] items-center justify-center rounded-[1rem] bg-[linear-gradient(90deg,#dd412f_0%,#ef4444_100%)] px-7 py-3 text-[1rem] font-medium text-white shadow-[0_12px_24px_rgba(221,65,47,0.28)] transition duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_16px_34px_rgba(221,65,47,0.34)]">
                              Je veux aider
                            </span>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex flex-1 flex-col px-7 pb-8 pt-12 text-center">
                          <h3 className=" text-[1.5rem] font-semibold leading-[1.02] tracking-[-0.035em] text-[#111111]">
                            {item.title}
                          </h3>

                          <p className="mt-5 flex-1 text-[1rem] leading-8 text-black/58">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );

                    if (item.type === "modal" || item.type === "adhesion-modal") {
                      return (
                        <button
                          key={item.title}
                          type="button"
                          onClick={() => {
                            if (item.type === "modal") setDonModalOpen(true);
                            if (item.type === "adhesion-modal") setAdhesionModalOpen(true);
                          }}
                          className="block h-full text-left"
                        >
                          {cardContent}
                        </button>
                      );
                    }

                    return (
                      <a
                        key={item.title}
                        href={item.href}
                        target={item.href?.startsWith("http") ? "_blank" : undefined}
                        rel={item.href?.startsWith("http") ? "noreferrer" : undefined}
                        className="block h-full"
                      >
                        {cardContent}
                      </a>
                    );
                  })}
                </div>
            </div>
          </div>
        </section>

        <section
          id="contact-form"
          ref={(el) => (sectionRefs.current[6] = el)}
          className="relative pb-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mx-auto mb-14 max-w-3xl text-center">
              <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                Contact
              </p>

              <h2 className="mt-4 text-4xl font-semibold text-[#111111] md:text-6xl">
                Échanger avec
                <span className="block bg-gradient-to-r from-red-600 to-[#ff7a59] bg-clip-text text-transparent">
                  l’association
                </span>
              </h2>

              <p className="mt-6 text-base leading-8 text-black/62">
                Une question, un signalement ou une envie d’aider ? Contactez-nous
                simplement via ce formulaire.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="flex flex-col justify-between rounded-[2.2rem] border border-white/50 bg-white/80 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.04)] backdrop-blur-xl md:p-10">
                <div>
                  <h3 className="text-2xl font-semibold text-[#111111]">
                    Association Savoinimaux
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-black/60">
                    <br />
                    Boutique solidaire : 6 Rue Filaterie - 74150 Rumilly
                    <br />    
                    Ouverture le mercredi et le samedi de 14h à 18h.
                  </p>

                  <p className="mt-4 text-sm leading-7 text-black/60">
                    <span className="font-medium text-black">Mail :</span>{" "}
                    savoinimaux@gmail.com
                    <br />
                    <span className="font-medium text-black">Tel :</span> 06 44 68
                    52 53
                    <br />
                    Merci de privilégier les SMS ou messages vocaux, l’équipe vous
                    recontactera dès que possible.
                  </p>

                  <p className="mt-6 text-sm font-medium text-red-600">
                    En cas d’urgence, contactez un vétérinaire de garde.
                  </p>
                </div>

                <div className="mt-8 flex gap-4">
                  <a
                    href="https://www.facebook.com/Savoinimaux/"
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] transition hover:bg-red-50 hover:text-red-600"
                  >
                    <FacebookIcon className="h-5 w-5" />
                  </a>

                  <a
                    href="https://www.instagram.com/savoinimaux/"
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] transition hover:bg-red-50 hover:text-red-600"
                  >
                    <InstagramIcon className="h-5 w-5" />
                  </a>
                </div>

                <div className="mt-10 overflow-hidden rounded-xl border border-black/10 shadow-[0_10px_30px_rgba(0,0,0,0.05)]">
                  <iframe
                    title="Carte Savoinimaux"
                    src="https://www.google.com/maps?q=47%20Rue%20du%20Pont%20Neuf,%2074150%20Rumilly,%20France&z=14&output=embed"
                    className="h-[220px] w-full border-0"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="rounded-[2.2rem] border border-white/50 bg-white/80 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.04)] backdrop-blur-xl md:p-10">
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                    Formulaire
                  </p>

                  <h3 className="mt-3 text-2xl font-semibold text-[#111111] md:text-3xl">
                    Nous contacter
                  </h3>

                  <p className="mt-3 text-sm text-black/60">
                    Réponse généralement sous 24 à 48h.
                  </p>
                </div>

                <form className="space-y-5" onSubmit={handleContactSubmit}>
                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                      Nom / Prénom
                    </label>
                    <input
                      type="text"
                      placeholder="Jean Dupont"
                      value={contactForm.fullName}
                      onChange={(e) =>
                        handleContactChange("fullName", e.target.value)
                      }
                      className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label htmlFor="department" className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                        Département
                      </label>
                      <select
                        id="department"
                        value={contactForm.department}
                        onChange={(e) =>
                          handleContactChange("department", e.target.value)
                        }
                        className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                      >
                        <option value="">Sélectionner</option>
                        <option value="Savoie">Savoie</option>
                        <option value="Haute-Savoie">Haute-Savoie</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        placeholder="06 00 00 00 00"
                        value={contactForm.phone}
                        onChange={(e) =>
                          handleContactChange("phone", e.target.value)
                        }
                        className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                        E-mail
                      </label>
                      <input
                        type="email"
                        placeholder="email@email.com"
                        value={contactForm.email}
                        onChange={(e) =>
                          handleContactChange("email", e.target.value)
                        }
                        className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                      Message
                    </label>
                    <textarea
                      rows={6}
                      placeholder="Décrivez votre demande..."
                      value={contactForm.message}
                      onChange={(e) =>
                        handleContactChange("message", e.target.value)
                      }
                      className="w-full rounded-[1.6rem] border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                    />
                  </div>

                  {contactStatus.error && (
                    <p className="text-sm font-medium text-red-600">
                      {contactStatus.error}
                    </p>
                  )}

                  {contactStatus.success && (
                    <p className="text-sm font-medium text-green-600">
                      Votre message a bien été envoyé.
                    </p>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={contactStatus.loading}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-red-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_14px_45px_rgba(220,38,38,0.25)] transition hover:shadow-[0_20px_55px_rgba(220,38,38,0.28)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                    >
                      {contactStatus.loading
                        ? "Envoi en cours..."
                        : "Envoyer le message"}
                      {!contactStatus.loading && (
                        <span className="transition group-hover:translate-x-1">
                          →
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      {donModalOpen && (
  <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
    <button
      type="button"
      aria-label="Fermer la fenêtre"
      className="absolute inset-0 bg-black/45 backdrop-blur-[3px]"
      onClick={() => setDonModalOpen(false)}
    />

    <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-[2rem] border border-white/20 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(255,248,248,0.94))] shadow-[0_30px_100px_rgba(0,0,0,0.20)]">
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-red-100/70 blur-3xl" />
      <div className="absolute -left-10 bottom-0 h-36 w-36 rounded-full bg-[#f7d9d6]/60 blur-3xl" />

      <div className="relative z-10 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-black/40">
              Dons ponctuels
            </p>
            <h3 className="mt-3 text-3xl font-semibold text-[#111111] md:text-4xl">
              Soutenir Savoinimaux
            </h3>
            <p className="mt-4 max-w-xl text-sm leading-7 text-black/60 md:text-base">
              Vous pouvez aider l’association de plusieurs façons selon ce qui
              vous convient le mieux.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setDonModalOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[#111111] transition hover:bg-red-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-8 grid gap-4">
          <div className="rounded-[1.6rem] border border-black/8 bg-white/88 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
            <p className="text-xl font-semibold text-[#111111]">Par virement bancaire</p>
            <p className="mt-2 text-sm leading-7 text-black/58">
              Téléchargez directement le RIB de l’association pour effectuer un don par virement.
            </p>
            <div className="mt-4">
              <a
                href="/documents/RIB-Savoinimaux.pdf"
                download
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-red-600 to-red-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_34px_rgba(220,38,38,0.24)] transition hover:-translate-y-0.5"
              >
                Télécharger le RIB
              </a>
            </div>
          </div>

          <div className="rounded-[1.6rem] border border-black/8 bg-white/88 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
            <p className="text-xl font-semibold text-[#111111]">Via HelloAsso</p>
            <p className="mt-2 text-sm leading-7 text-black/58">
              Effectuez un don en ligne de manière simple et sécurisée via HelloAsso.
            </p>
            <div className="mt-4">
              <a
                href="https://www.helloasso.com/associations/association-savoinimaux"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-semibold text-[#111111] transition hover:bg-red-50"
              >
                Accéder à HelloAsso
              </a>
            </div>
          </div>

          <div className="rounded-[1.6rem] border border-black/8 bg-white/88 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
            <p className="text-xl font-semibold text-[#111111]">Par chèque</p>
            <p className="mt-2 text-sm leading-7 text-black/58">
              Vous pouvez adresser un chèque à l’ordre de :
            </p>
            <p className="mt-3 inline-flex rounded-full bg-red-50 px-4 py-2 text-sm font-semibold text-red-600">
              Savoinimaux
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
{adhesionModalOpen && (
  <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
    <button
      type="button"
      aria-label="Fermer la fenêtre"
      className="absolute inset-0 bg-black/45 backdrop-blur-[3px]"
      onClick={() => setAdhesionModalOpen(false)}
    />

    <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-[2rem] border border-white/20 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(255,248,248,0.94))] shadow-[0_30px_100px_rgba(0,0,0,0.20)]">
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-red-100/70 blur-3xl" />
      <div className="absolute -left-10 bottom-0 h-36 w-36 rounded-full bg-[#f7d9d6]/60 blur-3xl" />

      <div className="relative z-10 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-black/40">
              Adhésion
            </p>
            <h3 className="mt-3 text-3xl font-semibold text-[#111111] md:text-4xl">
              Rejoindre Savoinimaux
            </h3>
            <p className="mt-4 max-w-xl text-sm leading-7 text-black/60 md:text-base">
              L’adhésion permet de soutenir officiellement l’association et de participer à son action dans la durée.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setAdhesionModalOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[#111111] transition hover:bg-red-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-8 grid gap-4">
        <div className="rounded-[1.6rem] border border-black/8 bg-white/88 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
          <p className="text-xl font-semibold text-[#111111]">
            Adhérer via HelloAsso
          </p>

          <p className="mt-2 text-sm leading-7 text-black/58">
            Vous pouvez rejoindre l’association directement en ligne via HelloAsso.
          </p>

          <div className="mt-5">
            <a
              href="https://www.helloasso.com/associations/association-savoinimaux"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-red-600 to-red-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_34px_rgba(220,38,38,0.24)] transition hover:-translate-y-0.5"
            >
              Adhérer via HelloAsso
            </a>
          </div>
        </div>

        <div className="rounded-[1.6rem] border border-black/8 bg-white/88 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
          <p className="text-xl font-semibold text-[#111111]">
            Télécharger le formulaire d’adhésion
          </p>

          <p className="mt-2 text-sm leading-7 text-black/58">
            Vous pouvez aussi télécharger le formulaire papier, le remplir puis le transmettre à l’association.
          </p>

          <div className="mt-5">
            <a
              href="/documents/formulaire-adhesion-2026.pdf"
              download
              className="inline-flex items-center justify-center rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-semibold text-[#111111] transition hover:bg-red-50"
            >
              Télécharger le formulaire
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
}